"use client";

import React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowUpRight, ChevronDown } from "lucide-react";
import CdnImage from "@/components/CdnImage";
import PolymarketMarketCard from "@/app/components/PolymarketMarketCard";
import { useT } from "@/hooks/useT";
import { decodeHTMLEntities } from "@/lib/sanitize";
import { marketPageUrl } from "@/lib/source-url";
import { timeAgo } from "../../helpers/format";
import { CARD_BASE } from "../../helpers/styles";
import type { ActivityEvent } from "../../types";

// New Event Card (grouped markets) — system event with a Sparkles header
// chip matching NewMarketRow. Click header to expand inner markets.
export default function NewEventRow({
  event,
  expandedEvents,
  toggleEventExpansion,
}: {
  event: ActivityEvent;
  expandedEvents: Set<string>;
  toggleEventExpansion: (eventId: string) => void;
}) {
  const t = useT();
  const router = useRouter();

  const marketCount = event.metadata?.market_count || event.metadata?.markets?.length || 1;
  const markets = event.metadata?.markets || [];
  const isExpanded = expandedEvents.has(event.id);
  const hasMarkets = markets.length > 0;
  const isKalshiEvent = event.market_slug?.toLowerCase().startsWith('kx');

  const eventHref = event.event_slug ? `/event/${event.event_slug}` : null;

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('[role="button"]')) {
      return;
    }
    if (hasMarkets) {
      toggleEventExpansion(event.id);
    } else if (eventHref) {
      router.push(eventHref);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      onClick={handleCardClick}
      className={CARD_BASE}
    >
      <div className="py-2.5 px-1">
        <div className="flex items-center gap-2.5">
          <span className="flex items-center justify-center w-8 h-8 rounded-[5px] flex-shrink-0 bg-gray-100 dark:bg-white/[0.06] text-gray-500 dark:text-gray-400">
            <Sparkles className="w-4 h-4" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                <span className="text-sm font-bold text-gray-900 dark:text-white">
                  {t("activity.newEvent.label") || "New event"}
                </span>
                <span className={`inline-flex items-center justify-center w-5 h-5 rounded-[4px] flex-shrink-0 ${
                  isKalshiEvent ? 'bg-emerald-500/15' : 'bg-blue-500/15'
                }`}>
                  <img
                    src={isKalshiEvent ? '/logos/kalshi/k-icon.svg' : '/logos/polymarket/icon-white.png'}
                    alt={isKalshiEvent ? 'Kalshi' : 'Polymarket'}
                    className="w-3.5 h-3.5 object-contain"
                  />
                </span>
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-[5px] text-[10px] font-semibold bg-gray-100 dark:bg-white/[0.05] text-gray-600 dark:text-gray-400 whitespace-nowrap">
                  {marketCount} market{marketCount !== 1 ? 's' : ''}
                </span>
              </div>
              <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-[5px] bg-gray-100 dark:bg-white/[0.05] border border-gray-200 dark:border-white/[0.06] text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {timeAgo(event.event_at, t)}
              </span>
            </div>
          </div>
        </div>

        {/* Slim event card — clickable for expansion or external link */}
        <div className="block mt-2.5 p-2.5 rounded-[5px] bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:border-gray-300 dark:hover:border-white/[0.1] transition-colors">
          <div className="flex items-center gap-2.5">
            {event.market_image ? (
              <CdnImage
                src={event.market_image}
                alt={event.market_title || "Event"}
                width={32}
                height={32}
                sizes="32px"
                className="w-8 h-8 rounded-[5px] object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-8 h-8 rounded-[5px] bg-gray-200 dark:bg-white/[0.06] flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {decodeHTMLEntities(event.market_title || '')}
              </h3>
              {event.description && !isExpanded && (
                <p className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
                  {decodeHTMLEntities(event.description)}
                </p>
              )}
            </div>
            {hasMarkets ? (
              <motion.div
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                className="flex-shrink-0"
              >
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </motion.div>
            ) : event.market_slug ? (
              <Link
                href={event.metadata?.source === 'hyperliquid'
                  ? `/hyperliquid/event/${encodeURIComponent(event.market_slug || '')}`
                  : isKalshiEvent
                    ? `/market/${event.market_slug}?source=kalshi`
                    : marketPageUrl({ marketSlug: event.market_slug, eventSlug: event.event_slug, outcome: event.outcome, source: (event.metadata?.source as 'polymarket' | 'predict') || 'polymarket', isMultiOutcome: event.is_multi_outcome })}
                onClick={(e) => e.stopPropagation()}
                className="flex-shrink-0"
              >
                <ArrowUpRight className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              </Link>
            ) : null}
          </div>
        </div>

        {/* Expanded inner markets */}
        <AnimatePresence>
          {isExpanded && hasMarkets && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="pt-2.5 space-y-2.5 max-h-[500px] overflow-y-auto">
                {markets.slice(0, 10).map((market: { slug: string; question: string }, idx: number) => (
                  <div key={market.slug || idx} onClick={(e) => e.stopPropagation()}>
                    <PolymarketMarketCard slug={market.slug} fallbackData={{ question: market.question }} />
                  </div>
                ))}
                {markets.length > 10 && (
                  <div className="text-center py-2">
                    <span className="text-[10px] text-gray-500">
                      +{markets.length - 10} more markets
                    </span>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
